import { call, put, takeEvery } from "redux-saga/effects";

import {
  CHAT_GET_CONTACTS,
  CHAT_GET_CONVERSATIONS,
  CHAT_CREATE_CONVERSATION,
  CHAT_GET_MESSAGES,
  CHAT_SEND_MESSAGE,
  CHAT_UPLOAD_FILE,
  CHAT_LOAD_MORE_MESSAGES,
} from "store/constants/Chat";
import {
  chatGetContactsSuccess,
  chatGetContactsFailure,
  chatGetConversationsSuccess,
  chatGetConversationsFailure,
  chatCreateConversationSuccess,
  chatCreateConversationFailure,
  chatGetMessagesSuccess,
  chatGetMessagesFailure,
  chatSendMessageSuccess,
  chatSendMessageFailure,
  chatUploadFileSuccess,
  chatUploadFileFailure,
  chatLoadMoreMessagesSuccess,
  chatGetMessages,
} from "store/actions/Chat";

import {
  fetchChatContacts,
  fetchConversations,
  createConversation,
  fetchMessages,
  sendMessageApi,
  getUploadUrl,
  uploadFileToS3,
  saveFileMessage,
} from "services/chat/chatApi";
import { getSocket } from "services/chat/socketService";

function* getContacts() {
  try {
    const response = yield call(fetchChatContacts);
    if (response?.ok) {
      yield put(chatGetContactsSuccess(response.payload.payload));
    } else {
      yield put(chatGetContactsFailure("Failed to fetch contacts"));
    }
  } catch (error) {
    yield put(chatGetContactsFailure(error));
  }
}

function* getConversations() {
  try {
    const response = yield call(fetchConversations);
    if (response?.ok) {
      yield put(chatGetConversationsSuccess(response.payload.payload));
    } else {
      yield put(chatGetConversationsFailure("Failed to fetch conversations"));
    }
  } catch (error) {
    yield put(chatGetConversationsFailure(error));
  }
}

function* createConversationSaga(action) {
  try {
    const { targetUserId, targetUserType } = action.payload;
    const response = yield call(createConversation, targetUserId, targetUserType);
    if (response?.ok) {
      yield put(chatCreateConversationSuccess(response.payload.payload));
    } else {
      yield put(chatCreateConversationFailure("Failed to create conversation"));
    }
  } catch (error) {
    yield put(chatCreateConversationFailure(error));
  }
}

function* getMessages(action) {
  try {
    const { conversationId, limit, offset } = action.payload;
    const response = yield call(fetchMessages, conversationId, limit, offset);
    if (response?.ok) {
      yield put(chatGetMessagesSuccess(response.payload.payload));
    } else {
      yield put(chatGetMessagesFailure("Failed to fetch messages"));
    }
  } catch (error) {
    yield put(chatGetMessagesFailure(error));
  }
}

function* sendMessage(action) {
  try {
    let { conversationId } = action.payload;
    const { content, messageType, parentMessageId, pending, targetUserId, targetUserType } = action.payload;

    // If this is a pending conversation, create it first
    if (pending && !conversationId) {
      const convResponse = yield call(createConversation, targetUserId, targetUserType);
      if (convResponse?.ok) {
        const conv = convResponse.payload.payload;
        conversationId = conv.conversationId;
        yield put(chatCreateConversationSuccess(conv));
        yield put(chatGetMessages({ conversationId }));
      } else {
        yield put(chatSendMessageFailure("Failed to create conversation"));
        return;
      }
    }

    const response = yield call(sendMessageApi, conversationId, content, messageType, parentMessageId);
    if (response?.ok) {
      const message = response.payload.payload;
      yield put(chatSendMessageSuccess(message));
      // Emit via socket so other participants receive it in real-time
      const socket = getSocket();
      if (socket) {
        socket.emit("message:new:broadcast", { conversationId, message });
      }
    } else {
      yield put(chatSendMessageFailure("Failed to send message"));
    }
  } catch (error) {
    yield put(chatSendMessageFailure(error));
  }
}

function* uploadFile(action) {
  try {
    const { conversationId, file } = action.payload;

    // Step 1: Get signed URL from backend
    const urlResponse = yield call(getUploadUrl, conversationId, file.name, file.type);
    if (!urlResponse?.ok) {
      yield put(chatUploadFileFailure("Failed to get upload URL"));
      return;
    }

    const { uploadUrl, filePath } = urlResponse.payload.payload;

    // Step 2: Upload file directly to S3
    yield call(uploadFileToS3, uploadUrl, file);

    // Step 3: Save file message in backend
    const response = yield call(saveFileMessage, conversationId, file.name, filePath, file.type, file.size);
    if (response?.ok) {
      const message = response.payload.payload;
      yield put(chatUploadFileSuccess(message));
      // Emit via socket so other participants receive it in real-time
      const socket = getSocket();
      if (socket) {
        socket.emit("message:new:broadcast", { conversationId, message });
      }
    } else {
      yield put(chatUploadFileFailure("Failed to save file message"));
    }
  } catch (error) {
    yield put(chatUploadFileFailure(error));
  }
}

function* loadMoreMessages(action) {
  try {
    const { conversationId, limit, offset } = action.payload;
    const response = yield call(fetchMessages, conversationId, limit, offset);
    if (response?.ok) {
      yield put(chatLoadMoreMessagesSuccess(response.payload.payload));
    }
  } catch (error) {
    console.error("Failed to load more messages", error);
  }
}

export default function* chatSaga() {
  yield takeEvery(CHAT_GET_CONTACTS, getContacts);
  yield takeEvery(CHAT_GET_CONVERSATIONS, getConversations);
  yield takeEvery(CHAT_CREATE_CONVERSATION, createConversationSaga);
  yield takeEvery(CHAT_GET_MESSAGES, getMessages);
  yield takeEvery(CHAT_SEND_MESSAGE, sendMessage);
  yield takeEvery(CHAT_UPLOAD_FILE, uploadFile);
  yield takeEvery(CHAT_LOAD_MORE_MESSAGES, loadMoreMessages);
}
